import React, { useEffect, useState, useCallback } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid,
} from "recharts";
import { Sliders, Clock, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { fmtEUR, fmtSigned, MONTHS_FR } from "@/lib/format";
import { CARD, Label, Loader, ErrorRetry } from "@/components/common";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label as UiLabel } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const now = new Date();

function CashChart({ data, threshold }) {
  return (
    <div className="h-64" data-testid="prevision-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
          <defs>
            <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#065F46" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#065F46" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F2F4" />
          <XAxis dataKey="short" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
          <Tooltip formatter={(v) => fmtEUR(v)} labelFormatter={(l, p) => p?.[0]?.payload?.label || l} />
          {threshold > 0 && <ReferenceLine y={threshold} stroke="#F59E0B" strokeDasharray="4 4" />}
          <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="4 4" />
          <Area type="monotone" dataKey="tresorerie" stroke="#065F46" strokeWidth={2.5} fill="url(#pg)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Simulator({ threshold }) {
  const [ca, setCa] = useState(0);
  const [exp, setExp] = useState(0);
  const [proto, setProto] = useState(0);
  const [extraExpense, setExtraExpense] = useState("");
  const [extraIncome, setExtraIncome] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/simulate", {
        ca_delta_pct: ca, expense_delta_pct: exp, prothese_delta_pct: proto,
        extra_expense: parseFloat(extraExpense) || 0, extra_income: parseFloat(extraIncome) || 0,
      });
      setRes(data);
    } finally { setLoading(false); }
  }, [ca, exp, proto, extraExpense, extraIncome]);

  useEffect(() => { const t = setTimeout(run, 300); return () => clearTimeout(t); }, [run]);

  return (
    <div className="space-y-5">
      <div className={`${CARD} p-6`}>
        <div className="flex items-center gap-2 text-gray-400"><Sliders className="h-4 w-4" /><Label>Simulateur « Et si ? »</Label></div>
        <div className="mt-5 space-y-6">
          <div>
            <div className="flex justify-between text-sm"><UiLabel>Variation du CA</UiLabel><span className="font-bold text-[#065F46]">{ca > 0 ? "+" : ""}{ca}%</span></div>
            <Slider value={[ca]} onValueChange={(v) => setCa(v[0])} min={-50} max={50} step={5} className="mt-3" data-testid="sim-ca" />
          </div>
          <div>
            <div className="flex justify-between text-sm"><UiLabel>Variation des dépenses</UiLabel><span className="font-bold text-amber-600">{exp > 0 ? "+" : ""}{exp}%</span></div>
            <Slider value={[exp]} onValueChange={(v) => setExp(v[0])} min={-50} max={50} step={5} className="mt-3" data-testid="sim-exp" />
          </div>
          <div>
            <div className="flex justify-between text-sm"><UiLabel>Variation des prothèses</UiLabel><span className="font-bold text-rose-600">{proto > 0 ? "+" : ""}{proto}%</span></div>
            <Slider value={[proto]} onValueChange={(v) => setProto(v[0])} min={-50} max={50} step={5} className="mt-3" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><UiLabel>Recette en + / mois (€)</UiLabel><Input type="number" value={extraIncome} onChange={(e) => setExtraIncome(e.target.value)} className="mt-1.5 rounded-xl" placeholder="0" /></div>
            <div><UiLabel>Dépense en + / mois (€)</UiLabel><Input type="number" value={extraExpense} onChange={(e) => setExtraExpense(e.target.value)} className="mt-1.5 rounded-xl" placeholder="0" /></div>
          </div>
        </div>
      </div>

      {res && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className={`${CARD} p-5`}><Label>Résultat mois courant</Label><p className={`mt-1 font-display text-2xl font-black ${res.result_next >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtSigned(res.result_next)}</p></div>
            <div className={`${CARD} p-5`}><Label>Trésorerie fin de mois</Label><p className="mt-1 font-display text-2xl font-black">{fmtEUR(res.treasury_end)}</p></div>
          </div>
          {res.first_negative_month && (
            <div className={`${CARD} bg-rose-50 p-4 text-sm font-semibold text-rose-700`} data-testid="sim-negative">
              ⚠ Avec ce scénario, la trésorerie devient négative en {res.first_negative_month.toLowerCase()}.
            </div>
          )}
          <div className={`${CARD} p-6`}>
            <Label className="mb-3">Trajectoire simulée sur 12 mois</Label>
            {loading ? <Loader /> : <CashChart data={res.twelve_months} threshold={threshold} />}
          </div>
        </>
      )}
    </div>
  );
}

function Calendar() {
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/calendar?year=${ym.year}&month=${ym.month}`).then((r) => setDays(r.data.days || {})).finally(() => setLoading(false));
  }, [ym]);

  const sorted = Object.keys(days).sort();
  return (
    <div className={`${CARD} p-6`}>
      <div className="mb-4 flex items-center gap-2 text-gray-400"><CalendarDays className="h-4 w-4" /><Label>Calendrier de trésorerie — {MONTHS_FR[ym.month]} {ym.year}</Label></div>
      {loading ? <Loader /> : sorted.length === 0 ? <p className="text-sm text-gray-400">Aucune opération ce mois-ci.</p> : (
        <div className="space-y-3">
          {sorted.map((d) => {
            const items = days[d];
            const day = new Date(d).getDate();
            return (
              <div key={d} className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-2xl bg-gray-50">
                  <span className="font-display text-lg font-bold leading-none">{day}</span>
                </div>
                <div className="flex-1 space-y-1 border-b border-gray-50 pb-3">
                  {items.map((t) => (
                    <div key={t.id} className="flex justify-between text-sm">
                      <span className="font-medium">{t.label}</span>
                      <span className={`font-semibold ${t.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>{t.type === "income" ? "+" : "−"}{fmtEUR(t.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Previsions() {
  const [proj, setProj] = useState([]);
  const [thirty, setThirty] = useState(null);
  const [threshold, setThreshold] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [p, d] = await Promise.all([api.get("/projection/twelve-months"), api.get("/dashboard")]);
      setProj(p.data); setThirty(d.data.thirty_days); setThreshold(d.data.settings?.safety_threshold || 0);
    } catch (e) { setError(true); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader />;
  if (error || !thirty) return <ErrorRetry onRetry={load} />;

  return (
    <div>
      <div>
        <p className="text-sm text-gray-400">Où va votre trésorerie</p>
        <h1 className="font-display text-2xl font-bold sm:text-3xl">Prévisions</h1>
      </div>

      <Tabs defaultValue="12m" className="mt-5">
        <TabsList className="rounded-full bg-gray-100 p-1">
          <TabsTrigger value="12m" className="rounded-full" data-testid="tab-12m">12 mois</TabsTrigger>
          <TabsTrigger value="30j" className="rounded-full" data-testid="tab-30j">30 jours</TabsTrigger>
          <TabsTrigger value="cal" className="rounded-full" data-testid="tab-cal">Calendrier</TabsTrigger>
          <TabsTrigger value="sim" className="rounded-full" data-testid="tab-sim">Et si ?</TabsTrigger>
        </TabsList>

        <TabsContent value="12m" className="mt-5 space-y-5">
          <div className={`${CARD} p-6`}>
            <Label className="mb-3">Trésorerie prévisionnelle sur 12 mois</Label>
            <CashChart data={proj} threshold={threshold} />
          </div>
          <div className={`${CARD} overflow-hidden`}>
            {proj.map((m, i) => (
              <div key={i} className={`flex items-center justify-between px-6 py-3 ${i % 2 ? "bg-gray-50/50" : ""}`}>
                <span className="text-sm font-semibold">{m.label}</span>
                <div className="flex gap-6 text-sm">
                  <span className="text-emerald-600">{fmtEUR(m.entrees)}</span>
                  <span className="text-rose-600">{fmtEUR(m.depenses)}</span>
                  <span className={`w-24 text-right font-bold ${m.tresorerie < 0 ? "text-rose-600" : ""}`}>{fmtEUR(m.tresorerie)}</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="30j" className="mt-5">
          <div className={`${CARD} p-8`}>
            <div className="flex items-center gap-2 text-gray-400"><Clock className="h-4 w-4" /><Label>Les 30 prochains jours</Label></div>
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between"><span className="text-gray-500">Aujourd'hui</span><span className="font-display text-2xl font-bold">{fmtEUR(thirty.aujourdhui)}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-500">Entrées attendues</span><span className="font-display text-xl font-bold text-emerald-600">+{fmtEUR(thirty.entrees_attendues)}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-500">Dépenses attendues</span><span className="font-display text-xl font-bold text-rose-600">−{fmtEUR(thirty.depenses_attendues)}</span></div>
              <div className="flex items-center justify-between border-t border-gray-100 pt-4"><span className="font-semibold">Dans 30 jours</span><span className="font-display text-3xl font-black text-[#065F46]">{fmtEUR(thirty.dans_30_jours)}</span></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cal" className="mt-5"><Calendar /></TabsContent>
        <TabsContent value="sim" className="mt-5"><Simulator threshold={threshold} /></TabsContent>
      </Tabs>
    </div>
  );
}
